// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

import React from "react";

const FONT  = "'Inter','Avenir Next','Century Gothic',sans-serif";
const TEAL  = "#4fd1c5";
const BG    = "#0f1117";
const PANEL = "#161b24";
const BORDER = "#2d3748";
const TEXT  = "#e8e6e1";
const MUTED = "#708898";
const GREEN = "#68d391";
const AMBER = "#f6ad55";

interface SecurityPageProps {
  onBack?: () => void;
}

// ── Small reusable components ────────────────────────────────────────────────

function Badge({ color, children }: { color: string; children: React.ReactNode }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: color + "18", border: `1px solid ${color}40`,
      color, borderRadius: 999, padding: "3px 10px",
      fontSize: 11, fontWeight: 600, letterSpacing: "0.04em",
      whiteSpace: "nowrap",
    }}>
      {children}
    </span>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 style={{
      fontSize: 20, fontWeight: 700, color: TEXT,
      margin: "0 0 6px", letterSpacing: "-0.02em",
    }}>{children}</h2>
  );
}

function SectionSubtitle({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ fontSize: 14, color: MUTED, margin: "0 0 24px", lineHeight: 1.6 }}>
      {children}
    </p>
  );
}

function Card({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{
      background: PANEL, border: `1px solid ${BORDER}`,
      borderRadius: 10, padding: "20px 22px",
      ...style,
    }}>
      {children}
    </div>
  );
}

function CheckRow({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10 }}>
      <span style={{ color: GREEN, fontSize: 14, marginTop: 1, flexShrink: 0 }}>✓</span>
      <span style={{ fontSize: 13, color: MUTED, lineHeight: 1.55 }}>{children}</span>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function SecurityPage({ onBack }: SecurityPageProps) {
  return (
    <div style={{ minHeight: "100vh", background: BG, fontFamily: FONT, color: TEXT }}>

      {/* ── Nav bar ── */}
      <nav style={{
        borderBottom: `1px solid ${BORDER}`, padding: "14px 32px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        position: "sticky", top: 0, background: BG, zIndex: 100,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            onClick={onBack}
            style={{ fontSize: 17, fontWeight: 800, cursor: onBack ? "pointer" : "default", userSelect: "none" }}
            title={onBack ? "Home" : undefined}
          >
            <span style={{ color: TEXT }}>Open</span>
            <span style={{ color: TEAL }}>Estimate</span>
          </span>
          <span style={{ color: BORDER, fontSize: 16, margin: "0 4px" }}>/</span>
          <span style={{ fontSize: 13, color: MUTED, fontWeight: 500 }}>Security</span>
        </div>
        {onBack && (
          <button onClick={onBack} style={{
            background: "none", border: `1px solid ${BORDER}`, color: MUTED,
            borderRadius: 6, padding: "6px 16px", fontSize: 13, cursor: "pointer",
            fontFamily: FONT,
          }}>
            ← Back
          </button>
        )}
      </nav>

      <div style={{ maxWidth: 860, margin: "0 auto", padding: "56px 32px 80px" }}>

        {/* ── Hero ── */}
        <div style={{ marginBottom: 56, textAlign: "center" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: TEAL + "15", border: `1px solid ${TEAL}30`,
            borderRadius: 999, padding: "6px 16px", marginBottom: 24,
          }}>
            <span style={{ fontSize: 14 }}>🔒</span>
            <span style={{ fontSize: 12, color: TEAL, fontWeight: 600, letterSpacing: "0.06em" }}>
              SECURITY
            </span>
          </div>
          <h1 style={{
            fontSize: 38, fontWeight: 800, letterSpacing: "-0.03em",
            margin: "0 0 16px", lineHeight: 1.15,
          }}>
            Your data is protected.<br />
            <span style={{ color: TEAL }}>Here's exactly how.</span>
          </h1>
          <p style={{
            fontSize: 16, color: MUTED, maxWidth: 560, margin: "0 auto",
            lineHeight: 1.7,
          }}>
            Construction estimates contain sensitive financial and project data.
            We take that seriously — these are the concrete controls we have in
            place to keep your information safe.
          </p>
        </div>

        {/* ── Summary badges ── */}
        <div style={{
          display: "flex", flexWrap: "wrap", gap: 10,
          justifyContent: "center", marginBottom: 64,
        }}>
          <Badge color={GREEN}>✓ SOC 2 Infrastructure</Badge>
          <Badge color={GREEN}>✓ AES-256 Encryption at Rest</Badge>
          <Badge color={GREEN}>✓ TLS 1.2+ in Transit</Badge>
          <Badge color={GREEN}>✓ Row-Level Security</Badge>
          <Badge color={GREEN}>✓ GDPR Art. 17 Erasure</Badge>
          <Badge color={GREEN}>✓ Rate Limiting</Badge>
          <Badge color={GREEN}>✓ Audit Logging</Badge>
          <Badge color={GREEN}>✓ 0 Known Vulnerabilities</Badge>
        </div>

        {/* ── Grid sections ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 40 }}>

          {/* Infrastructure */}
          <Card>
            <div style={{ fontSize: 22, marginBottom: 12 }}>🏗️</div>
            <SectionTitle>Infrastructure</SectionTitle>
            <SectionSubtitle>
              Built entirely on SOC 2 Type II certified platforms — we don't operate our own servers.
            </SectionSubtitle>
            <CheckRow>
              <strong style={{ color: TEXT }}>Supabase</strong> — database, auth, and storage.
              SOC 2 Type II certified, hosted on AWS us-east-1.
            </CheckRow>
            <CheckRow>
              <strong style={{ color: TEXT }}>Vercel</strong> — serverless API and frontend hosting.
              SOC 2 Type II certified, global edge network.
            </CheckRow>
            <CheckRow>
              <strong style={{ color: TEXT }}>Anthropic</strong> — AI import feature only.
              SOC 2 Type II certified. Your data is not used to train their models.
            </CheckRow>
          </Card>

          {/* Encryption */}
          <Card>
            <div style={{ fontSize: 22, marginBottom: 12 }}>🔐</div>
            <SectionTitle>Encryption</SectionTitle>
            <SectionSubtitle>
              Your data is encrypted whether it's moving or sitting still.
            </SectionSubtitle>
            <CheckRow>
              <strong style={{ color: TEXT }}>In transit:</strong> All connections use TLS 1.2 or
              higher — the same standard used by banks. Data is never sent unencrypted.
            </CheckRow>
            <CheckRow>
              <strong style={{ color: TEXT }}>At rest:</strong> All database storage is encrypted
              with AES-256 at the infrastructure level via AWS.
            </CheckRow>
            <CheckRow>
              <strong style={{ color: TEXT }}>Auth tokens:</strong> Session tokens are stored in
              sessionStorage (not localStorage), limiting exposure to the active browser tab.
            </CheckRow>
          </Card>

          {/* Access Control */}
          <Card>
            <div style={{ fontSize: 22, marginBottom: 12 }}>🛡️</div>
            <SectionTitle>Access Control</SectionTitle>
            <SectionSubtitle>
              Every database query is gated at the row level — users can only ever read or write their own data.
            </SectionSubtitle>
            <CheckRow>
              <strong style={{ color: TEXT }}>Row-Level Security (RLS)</strong> is enforced on
              every table in the database. Even if an API bug occurred, one user
              could never access another user's projects.
            </CheckRow>
            <CheckRow>
              <strong style={{ color: TEXT }}>No privilege escalation:</strong> Subscription status,
              approval flags, and billing data are server-only columns — users
              cannot modify them via the client.
            </CheckRow>
            <CheckRow>
              <strong style={{ color: TEXT }}>Rate limiting:</strong> All API endpoints cap requests
              per user per minute to prevent abuse and runaway API costs.
            </CheckRow>
          </Card>

          {/* Privacy & GDPR */}
          <Card>
            <div style={{ fontSize: 22, marginBottom: 12 }}>📋</div>
            <SectionTitle>Privacy & GDPR</SectionTitle>
            <SectionSubtitle>
              You own your data. We provide the controls to exercise that ownership.
            </SectionSubtitle>
            <CheckRow>
              <strong style={{ color: TEXT }}>Right to portability (Art. 20):</strong> Export a
              complete JSON archive of your profile, all projects, snapshots, and
              audit history at any time — from Settings → Privacy & Data.
            </CheckRow>
            <CheckRow>
              <strong style={{ color: TEXT }}>Right to erasure (Art. 17):</strong> Account deletion
              permanently removes your profile, projects, and auth record from our
              systems — not just a soft delete.
            </CheckRow>
            <CheckRow>
              <strong style={{ color: TEXT }}>Data residency:</strong> All data is stored in the
              United States (AWS us-east-1).
            </CheckRow>
          </Card>

          {/* Authentication */}
          <Card>
            <div style={{ fontSize: 22, marginBottom: 12 }}>🔑</div>
            <SectionTitle>Authentication</SectionTitle>
            <SectionSubtitle>
              Secure identity management without storing passwords ourselves.
            </SectionSubtitle>
            <CheckRow>
              <strong style={{ color: TEXT }}>Passwords</strong> are hashed using bcrypt by
              Supabase Auth — we never store plaintext credentials.
              Minimum 10-character requirement enforced.
            </CheckRow>
            <CheckRow>
              <strong style={{ color: TEXT }}>Google OAuth</strong> is available with a
              pinned redirect URI, preventing open-redirect attacks.
            </CheckRow>
            <CheckRow>
              <strong style={{ color: TEXT }}>OAuth integrations</strong> (e.g. Procore) use
              one-time cryptographic nonces for the state parameter — your
              session token never appears in a redirect URL.
            </CheckRow>
          </Card>

          {/* Audit & Monitoring */}
          <Card>
            <div style={{ fontSize: 22, marginBottom: 12 }}>📊</div>
            <SectionTitle>Audit & Monitoring</SectionTitle>
            <SectionSubtitle>
              Every significant action is logged server-side and is available to you.
            </SectionSubtitle>
            <CheckRow>
              <strong style={{ color: TEXT }}>Immutable audit log:</strong> Project creates,
              deletes, snapshot actions, and sharing events are logged via a
              server-side RPC — clients cannot forge or modify log entries.
            </CheckRow>
            <CheckRow>
              <strong style={{ color: TEXT }}>No stack traces to clients:</strong> Internal errors
              are logged server-side only. API responses return generic messages —
              never internal file paths or library details.
            </CheckRow>
            <CheckRow>
              <strong style={{ color: TEXT }}>Snapshot immutability:</strong> Once saved, a
              snapshot's state, project ID, and creator cannot be altered.
            </CheckRow>
          </Card>

        </div>

        {/* ── HTTP Security Headers ── */}
        <Card style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 22, marginBottom: 12 }}>🌐</div>
          <SectionTitle>HTTP Security Headers</SectionTitle>
          <SectionSubtitle>
            Every response from open-estimate.com includes the following browser-enforced security headers.
          </SectionSubtitle>
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 32px",
          }}>
            {[
              ["Content-Security-Policy", "Restricts which scripts, styles, and connections the browser will permit"],
              ["Strict-Transport-Security", "Forces HTTPS for 2 years, including subdomains"],
              ["X-Frame-Options: DENY", "Prevents the app from being embedded in an iframe (clickjacking)"],
              ["X-Content-Type-Options", "Prevents MIME-type sniffing attacks"],
              ["Referrer-Policy", "Limits referrer information sent to third parties"],
              ["Permissions-Policy", "Disables camera, microphone, and geolocation APIs"],
              ["Cross-Origin-Opener-Policy", "Isolates browsing context from other origins"],
              ["Cross-Origin-Resource-Policy", "Restricts cross-origin resource sharing"],
            ].map(([header, desc]) => (
              <div key={header} style={{ marginBottom: 6 }}>
                <code style={{ fontSize: 11, color: TEAL, background: TEAL + "12", padding: "2px 6px", borderRadius: 4 }}>
                  {header}
                </code>
                <p style={{ fontSize: 12, color: MUTED, margin: "4px 0 0", lineHeight: 1.5 }}>{desc}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* ── Vendor Certifications ── */}
        <Card style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 22, marginBottom: 12 }}>🏆</div>
          <SectionTitle>Vendor Certifications</SectionTitle>
          <SectionSubtitle>
            Every vendor that touches your data has independently verified security controls.
          </SectionSubtitle>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${BORDER}` }}>
                  {["Vendor", "Role", "Certification"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "8px 12px", color: MUTED, fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ["Supabase", "Database, auth, storage", "SOC 2 Type II"],
                  ["Vercel", "Hosting, serverless API", "SOC 2 Type II"],
                  ["Anthropic", "AI import (optional feature)", "SOC 2 Type II"],
                  ["Stripe", "Payments (when live)", "PCI DSS Level 1"],
                  ["AWS", "Underlying infrastructure (via Supabase)", "SOC 2 Type II, ISO 27001"],
                ].map(([vendor, role, cert]) => (
                  <tr key={vendor} style={{ borderBottom: `1px solid ${BORDER}30` }}>
                    <td style={{ padding: "10px 12px", color: TEXT, fontWeight: 500 }}>{vendor}</td>
                    <td style={{ padding: "10px 12px", color: MUTED }}>{role}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <Badge color={GREEN}>{cert}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* ── Roadmap ── */}
        <Card style={{ marginBottom: 40 }}>
          <div style={{ fontSize: 22, marginBottom: 12 }}>🗺️</div>
          <SectionTitle>Security Roadmap</SectionTitle>
          <SectionSubtitle>
            Here's what we're working toward — and what we'll have before enterprise contracts.
          </SectionSubtitle>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {[
              { label: "Third-party penetration test", status: "planned", note: "Before first enterprise contract" },
              { label: "GitHub Dependabot (auto CVE PRs)", status: "planned", note: "Automated dependency scanning" },
              { label: "Secret scanning on all commits", status: "planned", note: "Prevent accidental key exposure" },
              { label: "SOC 2 Type II audit", status: "roadmap", note: "Targeted for 2027" },
              { label: "MFA enforcement option", status: "roadmap", note: "Per-org setting for enterprise teams" },
              { label: "EU data residency option", status: "roadmap", note: "For EU enterprise customers" },
            ].map(({ label, status, note }) => (
              <div key={label} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <Badge color={status === "planned" ? TEAL : AMBER}>
                  {status === "planned" ? "Planned" : "Roadmap"}
                </Badge>
                <div>
                  <div style={{ fontSize: 13, color: TEXT, fontWeight: 500 }}>{label}</div>
                  <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>{note}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* ── Responsible Disclosure ── */}
        <div style={{
          background: TEAL + "0d", border: `1px solid ${TEAL}30`,
          borderRadius: 10, padding: "28px 32px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          flexWrap: "wrap", gap: 20,
        }}>
          <div>
            <h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: TEXT }}>
              Found a vulnerability?
            </h3>
            <p style={{ margin: 0, fontSize: 13, color: MUTED, maxWidth: 480, lineHeight: 1.6 }}>
              We take all security reports seriously and respond within 24 hours.
              Please report responsibly — give us reasonable time to fix before
              public disclosure.
            </p>
          </div>
          <a
            href="mailto:security@open-estimate.com"
            style={{
              display: "inline-block", background: TEAL, color: BG,
              fontWeight: 700, fontSize: 13, textDecoration: "none",
              padding: "10px 22px", borderRadius: 6, whiteSpace: "nowrap",
              fontFamily: FONT,
            }}
          >
            security@open-estimate.com
          </a>
        </div>

        {/* ── Footer note ── */}
        <p style={{ fontSize: 12, color: MUTED, textAlign: "center", marginTop: 40, opacity: 0.6 }}>
          Last updated April 2026 · Questions? Contact{" "}
          <a href="mailto:security@open-estimate.com" style={{ color: TEAL, textDecoration: "none" }}>
            security@open-estimate.com
          </a>
        </p>

      </div>
    </div>
  );
}
